import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import MyEmojiPicker from "./MyEmojiPicker";


function DepartmentAnnouncementEditor() {
  const { user, authFetch } = useAuth();

    const [departments, setDepartments] = useState([]);
    const [departmentId, setDepartmentId] = useState(""); // load-from dept
    const [targetDeptIds, setTargetDeptIds] = useState([]); // save-to depts
    const [, setHtml] = useState("");
    const [loadingDept, setLoadingDept] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState(false);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");


  const editorRef = useRef(null);
  const emojiRef = useRef(null);
const [showEmojiPicker, setShowEmojiPicker] = useState(false);

const TARGET_DEPTS_STORAGE_KEY = "department_announcement_target_depts";

  const roleKey = String(user?.role || "").toLowerCase();
  const isSuperadmin = roleKey === "superadmin";
  const isManager = roleKey === "manager";

  const canManage = isSuperadmin || isManager;

  const myDeptId = useMemo(() => {
    const n = Number(user?.department_id);
    return Number.isFinite(n) ? n : null;
  }, [user?.department_id]);

  useEffect(() => {
    if (!canManage) return;

    let alive = true;

    async function loadDepartments() {
      try {
        setLoadingDept(true);
        setErr("");

        const res = await authFetch("/api/departments", { cache: "no-store" });
        const data = await res.json().catch(() => []);

        if (!res.ok) {
          throw new Error(data?.error || "Failed to load departments");
        }

        const rows = Array.isArray(data) ? data : [];
        if (!alive) return;

        setDepartments(rows);

        if (rows.length > 0) {
        let firstDeptId = String(rows[0].id);

        if (isManager && myDeptId) {
            const found = rows.find((d) => Number(d.id) === Number(myDeptId));
            if (found) {
            firstDeptId = String(found.id);
            }
        }

        setDepartmentId(firstDeptId);

        const saved = localStorage.getItem(TARGET_DEPTS_STORAGE_KEY);

        if (saved) {
        try {
            const parsed = JSON.parse(saved);

            const validIds = parsed.filter((id) =>
            rows.some((d) => String(d.id) === String(id))
            );

            setTargetDeptIds(validIds.length ? validIds : [firstDeptId]);
        } catch {
            setTargetDeptIds([firstDeptId]);
        }
        } else {
        setTargetDeptIds([firstDeptId]);
        }

        }

      } catch (e) {
        if (!alive) return;
        setErr(e.message || "Failed to load departments");
      } finally {
        if (alive) setLoadingDept(false);
      }
    }

    loadDepartments();

    return () => {
      alive = false;
    };
  }, [authFetch, canManage, isManager, isSuperadmin, myDeptId]);

  useEffect(() => {
    if (!canManage) return;
    if (!departmentId) return;

    let alive = true;

    async function loadAnnouncement() {
      try {
        setLoadingMessage(true);
        setErr("");
        setMsg("");

            const res = await authFetch(`/api/department-announcement?departmentId=${departmentId}`, {
            cache: "no-store",
            });

        const data = await res.json().catch(() => null);

        if (!res.ok) {
          throw new Error(data?.error || "Failed to load announcement");
        }

        const nextHtml = String(data?.message_html || "");
        if (!alive) return;

        setHtml(nextHtml);

        if (editorRef.current) {
          editorRef.current.innerHTML = nextHtml || "";
        }
      } catch (e) {
        if (!alive) return;
        setErr(e.message || "Failed to load announcement");
        setHtml("");
        if (editorRef.current) {
          editorRef.current.innerHTML = "";
        }
      } finally {
        if (alive) setLoadingMessage(false);
      }
    }

    loadAnnouncement();

    return () => {
      alive = false;
    };
  }, [authFetch, canManage, departmentId]);

  useEffect(() => {
  function handleClickOutside(event) {
    if (!emojiRef.current) return;
    if (!emojiRef.current.contains(event.target)) {
      setShowEmojiPicker(false);
    }
  }

  if (showEmojiPicker) {
    document.addEventListener("mousedown", handleClickOutside);
  }

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [showEmojiPicker]);

useEffect(() => {
  if (!departmentId) return;

  setTargetDeptIds((prev) => {
    if (prev.length > 0) return prev;
    return [String(departmentId)];
  });
}, [departmentId]);



  function syncFromEditor() {
    const next = editorRef.current?.innerHTML || "";
    setHtml(next);
    return next;
  }

  function focusEditor() {
    if (editorRef.current) editorRef.current.focus();
  }

  function exec(command, value = null) {
    focusEditor();
    document.execCommand(command, false, value);
    syncFromEditor();
  }

  function setColor(color) {
    exec("foreColor", color);
  }

  function insertEmoji(emoji) {
    focusEditor();
    document.execCommand("insertText", false, emoji);
    syncFromEditor();
  }

  function handleEmojiSelect(emoji) {
  insertEmoji(emoji);
  setShowEmojiPicker(false);
}


function toggleTargetDept(deptId) {
  const id = String(deptId);

  setTargetDeptIds((prev) => {
    const next = prev.includes(id)
      ? prev.filter((x) => x !== id)
      : [...prev, id];

    localStorage.setItem(
      TARGET_DEPTS_STORAGE_KEY,
      JSON.stringify(next)
    );

    return next;
  });
}

function selectAllDepartments() {
  const all = (departments || []).map((d) => String(d.id));
  setTargetDeptIds(all);
  localStorage.setItem(TARGET_DEPTS_STORAGE_KEY, JSON.stringify(all));
}

function clearDepartmentSelection() {
  setTargetDeptIds([]);
  localStorage.setItem(TARGET_DEPTS_STORAGE_KEY, JSON.stringify([]));
}

async function handleSave() {
  try {
    if (!targetDeptIds.length) {
      setErr("Please select at least one department");
      return;
    }

    setSaving(true);
    setErr("");
    setMsg("");

    const latestHtml = syncFromEditor();

    const results = await Promise.all(
      targetDeptIds.map(async (deptId) => {
        const res = await authFetch("/api/department-announcement", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            department_id: Number(deptId),
            message_html: latestHtml,
          }),
        });

        const data = await res.json().catch(() => null);

        if (!res.ok) {
          throw new Error(data?.error || `Failed to save announcement for department ${deptId}`);
        }

        return data;
      })
    );

    setMsg(`Announcement saved successfully for ${results.length} department(s).`);
  } catch (e) {
    setErr(e.message || "Failed to save announcement");
  } finally {
    setSaving(false);
  }
}

  async function handleClear() {
    setHtml("");
    setMsg("");
    setErr("");

    if (editorRef.current) {
      editorRef.current.innerHTML = "";
    }

    focusEditor();
  }

  if (!canManage) return null;

  return (
    <div className="ann-panel">
      <style>{`
        .ann-panel{
          background:#fff;
          border:1px solid rgba(2,6,23,.08);
          border-radius:12px;
          box-shadow:0 10px 22px rgba(2,6,23,.06);
          overflow:hidden;
        }

        .ann-head{
          padding:16px;
          border-bottom:1px solid rgba(2,6,23,.06);
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          flex-wrap:wrap;
        }

        .ann-title{
          margin:0;
          font-size:18px;
          font-weight:950;
          color:#0f172a;
        }

        .ann-sub{
          margin-top:4px;
          font-size:12px;
          font-weight:800;
          color:#64748b;
        }

        .ann-dept{
          display:flex;
          align-items:center;
          gap:8px;
          flex-wrap:wrap;
        }

        .ann-dept label{
          font-size:12px;
          font-weight:900;
          color:#475569;
        }

        .ann-dept select{
          height:38px;
          border-radius:10px;
          border:1px solid rgba(2,6,23,.10);
          background:#fff;
          padding:0 12px;
          font-size:12px;
          font-weight:800;
          color:#0f172a;
          min-width:180px;
        }

        .ann-toolbar{
          padding:12px 16px;
          border-bottom:1px solid rgba(2,6,23,.06);
          display:flex;
          gap:8px;
          flex-wrap:wrap;
          align-items:center;
          background:#f8fafc;
        }

        .ann-tool-btn{
          border:1px solid rgba(2,6,23,.10);
          background:#fff;
          color:#0f172a;
          height:36px;
          min-width:36px;
          padding:0 10px;
          border-radius:10px;
          cursor:pointer;
          font-size:13px;
          font-weight:900;
          box-shadow:0 6px 14px rgba(2,6,23,.05);
        }

        .ann-tool-btn:hover{
          transform:translateY(-1px);
        }

        .ann-color-row,
        .ann-emoji-row{
          display:flex;
          align-items:center;
          gap:6px;
          flex-wrap:wrap;
        }

        .ann-color-dot{
          width:22px;
          height:22px;
          border-radius:999px;
          border:2px solid #fff;
          box-shadow:0 0 0 1px rgba(2,6,23,.10);
          cursor:pointer;
        }

        .ann-body{
          padding:16px;
        }

        .ann-editor{
          min-height:170px;
          border:1px solid rgba(2,6,23,.10);
          border-radius:12px;
          padding:14px;
          background:#fff;
          outline:none;
          font-size:14px;
          color:#0f172a;
          line-height:1.6;
        }

        .ann-editor:empty:before{
          content: attr(data-placeholder);
          color:#94a3b8;
          font-weight:700;
        }

        .ann-actions{
          margin-top:12px;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          flex-wrap:wrap;
        }

        .ann-left-msg{
          min-height:20px;
          font-size:12px;
          font-weight:800;
        }

        .ann-ok{ color:#15803d; }
        .ann-err{ color:#b91c1c; }

        .ann-right-actions{
          display:flex;
          gap:8px;
          flex-wrap:wrap;
        }

        .ann-btn{
          height:38px;
          border:none;
          border-radius:10px;
          padding:0 14px;
          cursor:pointer;
          font-weight:900;
          font-size:13px;
        }

        .ann-btn-secondary{
          background:#e2e8f0;
          color:#0f172a;
        }

        .ann-btn-primary{
          background:#2563eb;
          color:#fff;
        }

        .ann-btn:disabled{
          opacity:.6;
          cursor:not-allowed;
        }

        .ann-targets{
        padding:12px 16px;
        border-bottom:1px solid rgba(2,6,23,.06);
        background:#fff;
        }

        .ann-targets-top{
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap:12px;
        flex-wrap:wrap;
        }

        .ann-targets-title{
        font-size:12px;
        font-weight:900;
        color:#475569;
        text-transform:uppercase;
        letter-spacing:.04em;
        }

        .ann-target-actions{
        display:flex;
        gap:8px;
        flex-wrap:wrap;
        }

        .ann-target-list{
        margin-top:10px;
        display:flex;
        gap:10px;
        flex-wrap:wrap;
        }

        .ann-check-item{
        display:inline-flex;
        align-items:center;
        gap:8px;
        padding:8px 10px;
        border:1px solid rgba(2,6,23,.08);
        border-radius:10px;
        background:#f8fafc;
        font-size:12px;
        font-weight:800;
        color:#0f172a;
        cursor:pointer;
        }

        .ann-check-item input{
        margin:0;
        }

      `}</style>

      <div className="ann-head">
        <div>
          <h3 className="ann-title">Department Announcement</h3>
          <div className="ann-sub">
            Managers and superadmins can post one message for the department.
          </div>
        </div>

        <div className="ann-dept">
          <label htmlFor="ann-department">Load From</label>
            <select
                onChange={(e) => {
                const nextId = e.target.value;
                setDepartmentId(nextId);

                setTargetDeptIds((prev) => {
                    const next = prev.includes(nextId) ? prev : [nextId, ...prev];
                    localStorage.setItem(
                    TARGET_DEPTS_STORAGE_KEY,
                    JSON.stringify(next)
                    );
                    return next;
                });
                }}
            >          
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="ann-targets">
  <div className="ann-targets-top">
    <div className="ann-targets-title">Send To Departments</div>

    <div className="ann-target-actions">
      <button type="button" className="ann-tool-btn" onClick={selectAllDepartments}>
        Select All
      </button>
      <button type="button" className="ann-tool-btn" onClick={clearDepartmentSelection}>
        Clear
      </button>
    </div>
  </div>

  <div className="ann-target-list">
    {departments.map((d) => {
      const checked = targetDeptIds.includes(String(d.id));
      return (
        <label key={d.id} className="ann-check-item">
          <input
            type="checkbox"
            checked={checked}
            onChange={() => toggleTargetDept(d.id)}
          />
          <span>{d.name}</span>
        </label>
      );
    })}
  </div>
</div>


      <div className="ann-toolbar">
        <button type="button" className="ann-tool-btn" onClick={() => exec("bold")} title="Bold">
          B
        </button>
        <button type="button" className="ann-tool-btn" onClick={() => exec("italic")} title="Italic">
          I
        </button>
        <button type="button" className="ann-tool-btn" onClick={() => exec("underline")} title="Underline">
          U
        </button>

        <div className="ann-color-row">
          <button type="button" className="ann-tool-btn" onClick={() => setColor("#dc2626")} title="Red">
            A
          </button>
          <span className="ann-color-dot" style={{ background: "#dc2626" }} onClick={() => setColor("#dc2626")} />
          <span className="ann-color-dot" style={{ background: "#2563eb" }} onClick={() => setColor("#2563eb")} />
          <span className="ann-color-dot" style={{ background: "#16a34a" }} onClick={() => setColor("#16a34a")} />
          <span className="ann-color-dot" style={{ background: "#ca8a04" }} onClick={() => setColor("#ca8a04")} />
          <span className="ann-color-dot" style={{ background: "#7c3aed" }} onClick={() => setColor("#7c3aed")} />
        </div>

            <div
            className="ann-emoji-row"
            ref={emojiRef}
            style={{ position: "relative", zIndex: 20 }}
            >
            <button
                type="button"
                className="ann-tool-btn"
                onClick={() => setShowEmojiPicker((v) => !v)}
                title="Open emoji picker"
            >
                😊
            </button>

            {showEmojiPicker ? (
                <MyEmojiPicker onSelect={handleEmojiSelect} />
            ) : null}
            </div>

      </div>

      <div className="ann-body">
        <div
          ref={editorRef}
          className="ann-editor"
          contentEditable
          suppressContentEditableWarning
          data-placeholder={loadingMessage ? "Loading announcement..." : "Write department announcement here..."}
          onInput={syncFromEditor}
        />

        <div className="ann-actions">
          <div className={`ann-left-msg ${err ? "ann-err" : "ann-ok"}`}>
            {err ? err : msg}
          </div>

          <div className="ann-right-actions">
            <button
              type="button"
              className="ann-btn ann-btn-secondary"
              onClick={handleClear}
              disabled={saving || loadingMessage}
            >
              Clear
            </button>

            <button
              type="button"
              className="ann-btn ann-btn-primary"
              onClick={handleSave}
              disabled={saving || loadingDept || loadingMessage || !targetDeptIds.length}
            >
              {saving ? "Saving..." : "Save Announcement"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DepartmentAnnouncementEditor;
